'use client';

import { useTranslations } from 'next-intl';
import { Pencil, Trash2, CircleDot } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PermissionGate } from '@/features/auth';
import type { StorageLocation } from '../types';

interface StorageLocationCompactRowProps {
  location: StorageLocation;
  onEdit: (item: StorageLocation) => void;
  onDelete: (item: StorageLocation) => void;
}

const STATUS_STYLES: Record<string, string> = {
  active:
    'border-green-500/30 bg-green-500/10 text-green-600 dark:text-green-400',
  full: 'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400'
};

export function StorageLocationCompactRow({
  location,
  onEdit,
  onDelete
}: StorageLocationCompactRowProps) {
  const t = useTranslations('storageLocations');

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      {/* Left: name + code */}
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <span className='truncate font-medium'>{location.name}</span>
          <span className='text-muted-foreground hidden truncate font-mono text-sm sm:inline'>
            {location.code}
          </span>
        </div>
      </div>

      {/* Center: utilization + capacity (hidden on mobile) */}
      <span className='text-muted-foreground hidden text-sm md:inline'>
        {location.utilization}%
      </span>
      <span className='text-muted-foreground hidden text-sm md:inline'>
        {t('usedOfCapacity', {
          used: location.used_shelves,
          capacity: location.capacity
        })}
      </span>

      {/* Status badge */}
      <Badge
        variant='outline'
        className={cn(
          'shrink-0 text-xs uppercase',
          STATUS_STYLES[location.status]
        )}
      >
        <CircleDot className='me-1 h-3 w-3 fill-current' />
        {location.status === 'active' ? t('statusActive') : t('statusFull')}
      </Badge>

      {/* Actions */}
      <div className='flex shrink-0 items-center gap-1'>
        <PermissionGate permissions={['storage_locations.edit']}>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            onClick={() => onEdit(location)}
          >
            <Pencil className='h-3.5 w-3.5' />
          </Button>
        </PermissionGate>
        <PermissionGate permissions={['storage_locations.delete']}>
          <Button
            variant='ghost'
            size='icon'
            className='text-destructive h-7 w-7'
            onClick={() => onDelete(location)}
          >
            <Trash2 className='h-3.5 w-3.5' />
          </Button>
        </PermissionGate>
      </div>
    </div>
  );
}
